import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Tenant metadata stored client-side.
 * Extended information not included in session.
 */
export interface TenantMetadata {
  id: string;
  name: string;
  // Add more fields as needed (feature flags, subscription tier, etc.)
}

export interface OrganizationMetadata {
  id: string;
  tenantId: string;
  name: string;
}

/**
 * Tenant store state and actions.
 *
 * This store manages tenant context client-side, complementing
 * better-auth's session which handles authentication only.
 *
 * Use cases:
 * - Active tenant selection (for multi-tenant users)
 * - Tenant switching UX (loading states, optimistic updates)
 * - Recent tenants list for quick switching
 * - Tenant metadata caching
 */
interface TenantState {
  // Current active tenant
  activeTenantId: string | null;
  activeOrganizationId: string | null;

  // Tenant metadata cache
  tenants: Record<string, TenantMetadata>;
  organizations: Record<string, OrganizationMetadata>;

  // Recent tenants for quick switching (LRU, max 5)
  recentTenantIds: string[];

  // Switching state for UX
  isSwitching: boolean;
  switchingToId: string | null;

  // Actions
  setActiveTenant: (tenantId: string, metadata?: TenantMetadata) => void;
  setTenants: (tenants: TenantMetadata[]) => void;
  setOrganizations: (organizations: OrganizationMetadata[]) => void;
  setActiveOrganization: (organizationId: string) => void;
  clearTenant: () => void;
  startSwitching: (tenantId: string) => void;
  finishSwitching: () => void;
}

export const useTenantStore = create<TenantState>()(
  persist(
    (set, get) => ({
      activeTenantId: null,
      activeOrganizationId: null,
      tenants: {},
      organizations: {},
      recentTenantIds: [],
      isSwitching: false,
      switchingToId: null,

      setActiveTenant: (tenantId, metadata) => {
        const { recentTenantIds, tenants, organizations } = get();

        // Update recent tenants (LRU)
        const newRecent = [
          tenantId,
          ...recentTenantIds.filter((id) => id !== tenantId),
        ].slice(0, 5);

        // Update metadata if provided
        const newTenants = metadata
          ? { ...tenants, [tenantId]: metadata }
          : tenants;

        const tenantOrganizations = Object.values(organizations).filter(
          (organization) => organization.tenantId === tenantId,
        );
        set({
          activeTenantId: tenantId,
          // A sole authorized organization is not an arbitrary selection.
          // Selecting it keeps organization-required API calls usable.
          activeOrganizationId:
            tenantOrganizations.length === 1
              ? tenantOrganizations[0]!.id
              : null,
          recentTenantIds: newRecent,
          tenants: newTenants,
          isSwitching: false,
          switchingToId: null,
        });
      },

      setTenants: (tenantList) => {
        const { activeTenantId } = get();
        const newTenants: Record<string, TenantMetadata> = {};
        for (const tenant of tenantList) {
          newTenants[tenant.id] = tenant;
        }
        // If current activeTenantId is not in the new tenant list, reset it
        const shouldResetActive = activeTenantId && !newTenants[activeTenantId];
        set({
          tenants: newTenants,
          ...(shouldResetActive
            ? { activeTenantId: null, activeOrganizationId: null }
            : {}),
        });
      },

      setOrganizations: (organizationList) => {
        const organizations = Object.fromEntries(
          organizationList.map((organization) => [
            organization.id,
            organization,
          ]),
        );
        const { activeOrganizationId, activeTenantId } = get();
        const activeOrganization = activeOrganizationId
          ? organizations[activeOrganizationId]
          : undefined;
        const tenantOrganizations = activeTenantId
          ? Object.values(organizations).filter(
              (organization) => organization.tenantId === activeTenantId,
            )
          : [];
        set({
          organizations,
          ...(activeOrganization &&
          activeOrganization.tenantId === activeTenantId
            ? {}
            : {
                activeOrganizationId:
                  tenantOrganizations.length === 1
                    ? tenantOrganizations[0]!.id
                    : null,
              }),
        });
      },

      setActiveOrganization: (organizationId) => {
        const { activeTenantId, organizations } = get();
        const organization = organizations[organizationId];
        if (!organization || organization.tenantId !== activeTenantId) return;
        set({ activeOrganizationId: organizationId });
      },

      clearTenant: () => {
        set({
          activeTenantId: null,
          activeOrganizationId: null,
          isSwitching: false,
          switchingToId: null,
        });
      },

      startSwitching: (tenantId) => {
        set({ isSwitching: true, switchingToId: tenantId });
      },

      finishSwitching: () => {
        set({ isSwitching: false, switchingToId: null });
      },
    }),
    {
      name: "folio-tenant-storage",
      // Only persist these fields
      partialize: (state) => ({
        activeTenantId: state.activeTenantId,
        activeOrganizationId: state.activeOrganizationId,
        recentTenantIds: state.recentTenantIds,
        tenants: state.tenants,
      }),
    },
  ),
);

// Selector hooks for common patterns
export const useActiveTenant = () => {
  const { activeTenantId, tenants } = useTenantStore();
  return activeTenantId ? tenants[activeTenantId] : null;
};

export const useActiveTenantId = () => useTenantStore((s) => s.activeTenantId);
export const useActiveOrganizationId = () =>
  useTenantStore((s) => s.activeOrganizationId);

export const useIsSwitchingTenant = () => useTenantStore((s) => s.isSwitching);
